import React, { useContext, useState, useEffect, Fragment } from "react";
import { map } from "ramda";
import { UserContext } from "UserContext";
import { Box, Grid, makeStyles } from "@material-ui/core";
import MarcDateSelectionCard from "./MarcDateSelectionCard";
import MarcContentTypeCard from "./MarcContentTypeCard";
import MarcOrganizationSelectionCard from "./MarcOrganizationSelectionCard";
import MarcModuleSelectionCard from "./MarcModuleSelectionCard";
import { isBlank, notBlank } from "./utils";
import { postMarcRequest } from "./fetchUtils";
import SuccessDialog from "./SuccessDialog";

const getModuleUrl = (userId, orgId) =>
  `${process.env.REACT_APP_API_BASE_URL}/users/${userId}/modules?org-id=${orgId}&limit=all`;

const getMarcGenerationUrl = (orgId) =>
  `${process.env.REACT_APP_CATALOG_BASE_URL}/marc/organizations/${orgId}`;

const fetchModules = (userId, orgId, token) =>
  fetch(getModuleUrl(userId, orgId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

const mapModule = (module) => ({ id: module.id, name: module.displayName });

const useStyles = makeStyles((theme) => ({
  root: {
    background: theme.palette.secondary.light,
    height: "100%",
    overflowX: "hidden",
  },

  mainBody: {
    paddingTop: 54,
    maxWidth: "100%",
    margin: 0,
  },

  item: {
    maxWidth: 600,
    minWidth: 504,
  },

  errorHeading: {
    color: theme.palette.error.main,
    fontSize: 14,
    marginBottom: 5,
  },

  errorMessage: {
    color: theme.palette.error.main,
    marginBottom: 10,
  },
}));

const initialState = {
  showLoading: false,
  showSuccessDialog: false,
  disableExport: false,
  selectedOrganization: "",
  includeAlias: true,
  emailAddress: "",
  startDate: null,
  endDate: null,
  modules: [],
  includeDelete: false,
  contentTypes: [],
};

const MarcExport = () => {
  const classes = useStyles();
  const { token, user } = useContext(UserContext);
  const [state, setState] = useState(initialState);
  const [error, setError] = useState(null);

  const [modules, setModules] = useState([]);
  useEffect(() => {
    if (notBlank(user.organizations) && isBlank(state.selectedOrganization)) {
      handleStateChange("selectedOrganization")(user.organizations[0].id);
    }

    if (token && notBlank(state.selectedOrganization)) {
      fetchModules(user.id, state.selectedOrganization, token)
        .then((response) => response.json())
        .then((modules) => map(mapModule, modules))
        .then((modules) => {
          setModules(modules);
        });
    } else {
      setModules([]);
    }
  }, [token, user.id, user.organizations, state.selectedOrganization]);

  const handleStateChange = (name) => (value) => {
    setState((s) => ({ ...s, [name]: value }));
  };

  useEffect(() => {
    const startDate = state.startDate;
    const endDate = state.endDate;

    if (notBlank(startDate) && notBlank(endDate)) {
      handleStateChange("disableExport")(startDate > endDate);
    }

    handleStateChange("disableExport")(isBlank(state.emailAddress));
  }, [state.startDate, state.endDate, state.emailAddress]);

  const exportReport = (props) => {
    handleStateChange("showLoading")(true);

    const url = getMarcGenerationUrl(state.selectedOrganization);
    const request = {
      email: state.emailAddress,
      organizationId: state.selectedOrganization,
      startDate: state.startDate,
      endDate: state.endDate,
      contentTypes: state.contentTypes,
      moduleMediaIds: state.modules,
      useCustomSubdomain: state.includeAlias,
      currentOnly: !state.includeDelete,
    };

    postMarcRequest(url, props.token, request)
      .then(() => handleStateChange("showSuccessDialog")(true))
      .catch((err) => setError(err.message))
      .finally(() => handleStateChange("showLoading")(false));
  };

  return (
    <Fragment>
      <Grid
        container
        direction="row"
        spacing={3}
        justify="center"
        className={classes.mainBody}
      >
        {isBlank(modules) ? (
          <Grid item className={classes.item}>
            <div>Loading...</div>
          </Grid>
        ) : notBlank(error) ? (
          <Grid item className={classes.item}>
            <Box className={classes.errorHeading}>
              Error Requesting MARC Generation.
            </Box>
            <Box className={classes.errorMessage}>{error}</Box>
            <Box>
              {"If you continue to have any issues, please contact "}
              <a href="mailto:support@biblioboard.com">
                support@biblioboard.com
              </a>
            </Box>
          </Grid>
        ) : (
          <Fragment>
            <Grid
              item
              className={classes.item}
              md={6}
              container
              direction="column"
              spacing={2}
              justify="flex-start"
            >
              <Grid item className={classes.item}>
                <MarcOrganizationSelectionCard
                  state={state}
                  handleStateChange={handleStateChange}
                  organizations={user.organizations}
                />
              </Grid>

              <Grid item className={classes.item}>
                <MarcDateSelectionCard
                  state={state}
                  handleStateChange={handleStateChange}
                />
              </Grid>
            </Grid>

            <Grid
              item
              className={classes.item}
              md={6}
              container
              direction="column"
              spacing={2}
              justify="flex-start"
            >
              <Grid item className={classes.item}>
                <MarcContentTypeCard
                  state={state}
                  handleStateChange={handleStateChange}
                />
              </Grid>

              <Grid item className={classes.item}>
                <MarcModuleSelectionCard
                  state={state}
                  handleStateChange={handleStateChange}
                  modules={modules}
                  handleExportClicked={exportReport}
                />
              </Grid>
            </Grid>
          </Fragment>
        )}
      </Grid>
      <SuccessDialog
        open={state.showSuccessDialog}
        message="Request for MARC has been sent. Check email for download link."
        handleClose={() => handleStateChange("showSuccessDialog")(false)}
      />
    </Fragment>
  );
};

export default MarcExport;
